import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import i18n, {i18nInit} from './i18n/i18n';
import {useDispatch, useSelector} from "react-redux";
import {
    getTouchpointConfig,
    setAppConfig,
    setAppInput,
    setModelsRatesMapping,
    setRates,
    setTouchpointConfig, updateCookies
} from "./store/slices/appSlice";
import languageCodes from "./config/languageCodes";
import GATracker, {GAEventBuildObject} from "./helpers/GATracker";
import {logErr, logInfo, logWarn} from "./helpers/debugLog";

import CONST from "./config/CONST";
import isDomElement from "./helpers/isDomElement";

import WidgetPortal from "./components/WidgetPortal/WidgetPortal";

import axios from "axios";


function App(props) {

    const {
        config
    } = props;

    const dispatch = useDispatch();

    const [widgetTargetElt, setWidgetTargetElt] = useState(null);
    const [widgetViewMode, setWidgetViewMode] = useState(null);
    const [widgetInput, setWidgetInput] = useState(null);
    const [widgetClosed, setWidgetClosed] = useState(true);

    const [labelsLoaded, setLabelsLoaded] = useState(false);
    const [touchpointConfigurationFileLoaded, setTouchpointConfigurationFileLoaded] = useState(false);
    const [modelsRatesMappingFileLoaded, setModelsRatesMappingFileLoaded] = useState(false);
    const [ratesFileLoaded, setRatesFileLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [closeBtn, setCloseBtn] = useState(config.closeBtn);
    const [closeBtnCallback, setCloseBtnCallback] = useState(() => config.closeBtnCallback);


    const touchpointConfig = useSelector(getTouchpointConfig);

    // const [error, setError] = useState(null);

    // save appConfig in store
    useEffect(() => {
        if(config) {
            const _config = {...config};
            for (const [key, value] of Object.entries(_config)) {   // remove functions (not serializable)
                if(typeof value === 'function') {
                    delete _config[key];
                }
            }
            dispatch(setAppConfig(_config));
        }
    }, [dispatch,config]);


    const _setWidgetInput = (_widgetInput) => {
        if(_widgetInput) {
            if(!_widgetInput.customerType || _widgetInput.customerType.trim() === '') {
                _widgetInput.customerType = touchpointConfig.defaults.customerType;
            }

            if(!_widgetInput.EIM || _widgetInput.EIM.trim() === '') {
                _widgetInput.EIM = touchpointConfig.defaults.EIM;
            }

            dispatch(setAppInput(_widgetInput));
        }
        else {
            dispatch(setAppInput(null))
        }
        setWidgetInput(_widgetInput);
    }
    const _setWidgetClosed = (_isClosed) => {
        setWidgetClosed(_isClosed);
    }


    // track event "application.init"
    useEffect(() => {
        if(config && config.source) {
            GATracker.init({ "cookiesConsent": config.cookies?.performance });
            GATracker.trackEvent({
                name: GATracker.EVENTS.APPLICATION_INIT.name,
                category: GATracker.EVENTS.APPLICATION_INIT.category,
                action: GATracker.EVENTS.APPLICATION_INIT.action
            });
        }
    }, [config]);


    // define the open() and close() method, exposed to hosting page
    useEffect(() => {

        if(config && touchpointConfig) {

            logInfo('define the open() and close() method, exposed to hosting page');

            window[CONST.JS_NAMESPACE].open = async ({
                 targetElt,
                 input,
                 closeBtn,
                 closeBtnCallback,
                 enableConfirmCta,
                 enableSelfOnBoardingCta,
                 onConfirmCtaCallback
            }) => {

                if(typeof closeBtn !== 'undefined') {
                    setCloseBtn(closeBtn);
                }
                if(typeof closeBtnCallback === 'function') {
                    setCloseBtnCallback(() => closeBtnCallback);
                }

                const _config = {...config};
                for (const [key, value] of Object.entries(_config)) {   // remove functions (not serializable)
                    if(typeof value === 'function') {
                        delete _config[key];
                    }
                }

                // merge parameters passed to the open method with appConfig
                dispatch(setAppConfig({
                    ..._config,
                    closeBtn,
                    enableConfirmCta,
                    enableSelfOnBoardingCta
                }));

                // define global onConfirmCtaCallback function
                if(typeof onConfirmCtaCallback === 'function') {
                    window[CONST.JS_NAMESPACE].onConfirmCtaCallback = onConfirmCtaCallback;
                }

                // close widget if previously open
                window[CONST.JS_NAMESPACE].close(true);

                // view mode
                logInfo(`Open widget with input`, input);
                logInfo(`Open widget with targetElt`, targetElt);
                if(targetElt) {
                    logInfo(`Render widget in`, targetElt);
                    setWidgetViewMode(CONST.VIEWMODE_EMBED);
                }
                else {
                    logInfo(`Render widget in standalone (fullscreen) mode`);
                    setWidgetViewMode(CONST.VIEWMODE_STANDALONE);
                }

                // target element
                let _widgetTargetElt;
                if(isDomElement(targetElt)) {
                    _widgetTargetElt = targetElt;
                }
                else {
                    let _previousTargetElt = document.getElementById(CONST.DOM_APP_WIDGET_ID);
                    if(_previousTargetElt) {
                        _previousTargetElt.parentNode.removeChild(_previousTargetElt);
                    }
                    _widgetTargetElt = document.createElement('div');
                    _widgetTargetElt.id = CONST.DOM_APP_WIDGET_ID;
                    document.body.appendChild(_widgetTargetElt);
                }

                setWidgetTargetElt(_widgetTargetElt);

                // show loading indicator
                setIsLoading(true);


                _setWidgetInput(input);

                // show the widget!
                _setWidgetClosed(false);
                setIsLoading(false);

                // track open
                GATracker.trackEvent(GAEventBuildObject({
                    event: {
                        name: GATracker.EVENTS.WIDGET_OPEN.name,
                        category: GATracker.EVENTS.WIDGET_OPEN.category,
                        action: GATracker.EVENTS.WIDGET_OPEN.action,
                    },
                    appData: {
                        config: _config,
                        input: input
                    }
                }));

            }

            window[CONST.JS_NAMESPACE].updateCookies = (cookies) => {
                if (
                    cookies &&
                    typeof cookies === 'object' &&
                    (
                        cookies.hasOwnProperty('essential') ||
                        cookies.hasOwnProperty('performance') ||
                        cookies.hasOwnProperty('comfort') ||
                        cookies.hasOwnProperty('advertising')
                    )
                ) {

                    // update redux
                    dispatch(updateCookies(cookies));

                    // update window[CONST.JS_NAMESPACE+"_config"].cookies
                    if(window[CONST.JS_NAMESPACE+"_config"] && window[CONST.JS_NAMESPACE+"_config"].cookies) {
                        window[CONST.JS_NAMESPACE+"_config"].cookies = {
                            ...window[CONST.JS_NAMESPACE+"_config"].cookies,
                            ...cookies
                        }
                    }

                    // init GATracker again
                    if(cookies.hasOwnProperty('performance')) {
                        GATracker.init({"cookiesConsent": cookies?.performance});
                    }
                }
            }

            window[CONST.JS_NAMESPACE].close = (skipEventTracking) => {

                skipEventTracking = skipEventTracking || false;

                // close the widget!
                _setWidgetClosed(true);
                _setWidgetInput(null);

                if(!skipEventTracking) {
                    GATracker.trackEvent(GAEventBuildObject({
                        event: {
                            name: GATracker.EVENTS.WIDGET_CLOSE.name,
                            category: GATracker.EVENTS.WIDGET_CLOSE.category,
                            action: GATracker.EVENTS.WIDGET_CLOSE.action
                        },
                        appData: {
                            config: config,
                            input: widgetInput
                        }
                    }));
                }

            }

            window[CONST.JS_NAMESPACE].update = () => {
                window[CONST.JS_NAMESPACE].reset();
            }

            handleQueue();

        }

        // eslint-disable-next-line
    }, [config, touchpointConfig]);

    const handleQueue = () => {

        window[CONST.JS_NAMESPACE+"_jsApiReady"] = true;

        const allowedQueuedFunctions = ['open', 'close', 'update', 'updateCookies'];

        if(window[CONST.JS_NAMESPACE+"_queue"] && window[CONST.JS_NAMESPACE+"_queue"].length) {
            window[CONST.JS_NAMESPACE+"_queue"].forEach((q) => {
                if(allowedQueuedFunctions.includes(q.func)) {
                    logInfo(`Execute queued function (${q.func})`);
                    window[CONST.JS_NAMESPACE][q.func](q.args);
                }
                else {
                    logWarn(`Queued function (${q.func}) is not allowed`);
                }
            });
            delete window[CONST.JS_NAMESPACE+"_queue"];
        }
    }


    // load language
    useEffect(() => {

        const changeLanguage = (lng) => {
            i18n.changeLanguage(languageCodes[lng]);
            logInfo(`Set application language: ${languageCodes[lng]}`);
        };

        const loadLanguageLabels = async () => {
            logInfo('Loading labels...');
            const labelsJsonUrl = `${CONST.APP_BASEURL}/static/json/${config.source}/${CONST.VERSION}/i18n-${languageCodes[config.lang]}-${CONST.VERSION}.json`;
            const response = await axios.get(labelsJsonUrl);
            if(!response || !response.data) {
                logErr(`Error loading language file ${labelsJsonUrl}`);
                return;
            }
            logInfo('Labels loaded');

            i18nInit(response.data, config.lang);
            changeLanguage(config.lang);

            return response.data;
        }

        if(config && config.source) {
            loadLanguageLabels().then(r => setLabelsLoaded(true));
        }

    }, [config]);


    // load touchpoint configuration file
    useEffect(() => {

        const loadTouchpointConfiguration = async () => {
            logInfo('Loading touchpoint configuration...');
            const touchpointConfigurationJsonUrl = `${CONST.APP_BASEURL}/static/json/${config.source}/${CONST.VERSION}/config-${CONST.VERSION}.json`;
            const response = await axios.get(touchpointConfigurationJsonUrl);
            if(!response || !response.data) {
                logErr(`Error loading touchpoint configuration ${touchpointConfigurationJsonUrl}`);
                return;
            }
            logInfo('Touchpoint configuration loaded');

            dispatch(setTouchpointConfig(response.data));

            return response.data;
        }

        if(config && config.source) {
            loadTouchpointConfiguration().then(r => setTouchpointConfigurationFileLoaded(true));
        }

    }, [config, dispatch]);


    // load models / rates mapping file
    useEffect(() => {

        const loadModelsRatesMapping = async () => {
            logInfo('Loading models/rates mapping...');
            const modelsRatesMappingJsonUrl = `${CONST.APP_BASEURL}/static/json/${config.source}/${CONST.VERSION}/modelsRatesMapping-${CONST.VERSION}.json`;
            const response = await axios.get(modelsRatesMappingJsonUrl);
            if(!response || !response.data || !response.data.models) {
                logErr(`Error loading models/rates mapping ${modelsRatesMappingJsonUrl}`);
                return;
            }
            logInfo('Models/rates mapping loaded');

            dispatch(setModelsRatesMapping(response.data.models));

            return response.data;
        }

        if(config && config.source) {
            loadModelsRatesMapping().then(r => setModelsRatesMappingFileLoaded(true));
        }

    }, [config, dispatch]);


    // load rates file
    useEffect(() => {

        const loadRates = async () => {
            logInfo('Loading rates...');
            const ratesJsonUrl = `${CONST.APP_BASEURL}/static/json/${config.source}/${CONST.VERSION}/rates-${CONST.VERSION}.json`;
            const response = await axios.get(ratesJsonUrl);
            if(!response || !response.data) {
                logErr(`Error loading rates ${ratesJsonUrl}`);
                return;
            }
            logInfo('Rates loaded');

            dispatch(setRates(response.data));

            return response.data;
        }

        if(config && config.source) {
            loadRates().then(r => setRatesFileLoaded(true));
        }

    }, [config, dispatch]);

    /*
    if(
        !widgetTargetElt ||
        !widgetViewMode ||
        !widgetInput ||
        widgetClosed ||
        !labelsLoaded ||
        !touchpointConfigurationFileLoaded ||
        !modelsRatesMappingFileLoaded ||
        !ratesFileLoaded ||
        isLoading ||
        !touchpointConfig
    ) {
        return null;
    }
    */

    return (
        <>
            <WidgetPortal
                isClosed={widgetClosed}
                targetElt={widgetTargetElt}
                viewmode={widgetViewMode}
                input={widgetInput}
                isLoading={ isLoading || !labelsLoaded || !touchpointConfigurationFileLoaded || !modelsRatesMappingFileLoaded || !ratesFileLoaded }
                closeBtn={ typeof closeBtn !== 'undefined' ? closeBtn : config.closeBtn }
                closeBtnCallback={ typeof closeBtnCallback === 'function' ? closeBtnCallback : config.closeBtnCallback }
                // error={error}
            />
        </>
    );

}

App.propTypes = {
    config: PropTypes.object.isRequired
}

export default App;










